import Big from "big.js";
import omitBy from "lodash/omitBy";
import { getAppliedOfferByItems, isReceiptOffer } from "../get-applied-offer";
import {
	getAmountWithinRange,
	getDiscountAmount,
} from "../get-discount-amount";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemCalculatedValues,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import {
	$getItemMetadata,
	$getItemTotalRawPriceAddons,
	$getOptionMetadata,
	$getOptionTotalRawPriceAddons,
	BASE_SUBTOTAL_ADDONS,
} from "./_internals";
import { DISCOUNT_TYPE } from "~served/configs/enums";

export const $isOrderUnpaid = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>,
) => !order.is_paid && !order.is_cancelled;

export const $getSubtotalAddons = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	item,
	isCancelled,
}: {
	item: OrderItemCalculatedValues<
		OrderItemInputLike<TOrderItem, TOrderItemOption>
	>;
	isCancelled: boolean;
}) => {
	if (isCancelled) return BASE_SUBTOTAL_ADDONS;

	item.subtotal_addons = {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(item.subtotal_addons, (value) => value === null),
	};
	const { original_price_addons, subtotal, subtotal_addons } = item;

	const newOfferIsCustomValue = subtotal_addons.offer.is_custom_value;
	const newOfferMetadata = subtotal_addons.offer.metadata;
	const newOfferAmount =
		isReceiptOffer(newOfferMetadata) || newOfferIsCustomValue
			? subtotal_addons.offer.amount
			: getAppliedOfferByItems([item], newOfferMetadata).offerAmount;

	const newDiscountIsDivided = subtotal_addons.discount.is_divided;
	const newDiscountType = subtotal_addons.discount.type;
	const newDiscountValue = subtotal_addons.discount.value;
	const newDiscountAmount =
		newDiscountType === DISCOUNT_TYPE.PERCENTAGE || !newDiscountIsDivided
			? getDiscountAmount(subtotal, {
					type: newDiscountType,
					value: newDiscountValue,
				}).discountAmount
			: subtotal_addons.discount.amount;

	const newVatIsIncluded =
		!!original_price_addons.vat.percentage &&
		!!original_price_addons.vat.amount;
	const newVatPercentage = newVatIsIncluded
		? original_price_addons.vat.percentage
		: subtotal_addons.vat.percentage;
	const newServiceChargeIsIncluded =
		!!original_price_addons.service_charge.percentage &&
		!!original_price_addons.service_charge.amount;
	const newServiceChargePercentage = newServiceChargeIsIncluded
		? original_price_addons.service_charge.percentage
		: subtotal_addons.service_charge.percentage;
	const newServiceChargeBdIsIncluded =
		!!original_price_addons.service_charge_bd?.percentage &&
		!!original_price_addons.service_charge_bd?.amount;
	const newServiceChargeBdPercentage = newServiceChargeBdIsIncluded
		? (original_price_addons.service_charge_bd?.percentage ?? 0)
		: subtotal_addons.service_charge_bd.percentage;
	const newCardSurchargePercentage = subtotal_addons.card_surcharge.percentage;
	const newTaxPercentage = subtotal_addons.tax?.percentage ?? 0;

	let [newVatAmount, newServiceChargeAmount, newServiceChargeBdAmount] = [
		0, 0, 0,
	];
	const grossSales = bigMath.sub(
		subtotal,
		subtotal_addons.offer.amount,
		subtotal_addons.discount.amount,
	);
	const addedVatScRate = bigMath.add(
		newVatPercentage,
		newServiceChargePercentage,
	);
	newVatAmount = newVatIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newVatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					subtotal,
					$getItemTotalRawPriceAddons(item),
					subtotal_addons.offer.amount,
					subtotal_addons.discount.amount,
				),
				newVatPercentage,
			);
	newVatAmount = getAmountWithinRange(0, newVatAmount, subtotal);
	newServiceChargeAmount = newServiceChargeIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newServiceChargePercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					subtotal,
					$getItemTotalRawPriceAddons(item),
					subtotal_addons.offer.amount,
					subtotal_addons.discount.amount,
				),
				newServiceChargePercentage,
			);
	newServiceChargeAmount = getAmountWithinRange(
		0,
		newServiceChargeAmount,
		subtotal,
	);
	newServiceChargeBdAmount = newServiceChargeBdIsIncluded
		? new Big(
				new Big(
					new Big(subtotal).sub(
						new Big(subtotal).div(new Big(1).add(newServiceChargeBdPercentage)),
					),
				).mul(newServiceChargeBdPercentage),
			)
				.div(newServiceChargeBdPercentage || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(subtotal, $getItemTotalRawPriceAddons(item)),
				newServiceChargeBdPercentage,
			);
	newServiceChargeBdAmount = getAmountWithinRange(
		0,
		newServiceChargeBdAmount,
		subtotal,
	);
	let newTaxAmount = bigMath.mul(
		bigMath.add(
			bigMath.sub(
				subtotal,
				$getItemTotalRawPriceAddons(item),
				subtotal_addons.offer.amount,
				subtotal_addons.discount.amount,
			),
			newVatAmount,
			newServiceChargeAmount,
			newServiceChargeBdAmount,
		),
		newTaxPercentage,
	);
	newTaxAmount = getAmountWithinRange(0, newTaxAmount, subtotal);
	let newCardSurchargeAmount = bigMath.mul(
		bigMath.sub(
			subtotal,
			$getItemTotalRawPriceAddons(item),
			subtotal_addons.offer.amount,
			subtotal_addons.discount.amount,
		),
		newCardSurchargePercentage,
	);
	newCardSurchargeAmount = getAmountWithinRange(
		0,
		newCardSurchargeAmount,
		subtotal,
	);

	return {
		offer: {
			amount: newOfferAmount,
			metadata: newOfferAmount ? newOfferMetadata : undefined,
			is_custom_value: newOfferIsCustomValue,
		},
		discount: {
			is_divided: newDiscountIsDivided,
			type: newDiscountType,
			value: newDiscountValue,
			amount: newDiscountAmount,
		},
		vat: {
			is_included: newVatIsIncluded,
			percentage: newVatPercentage,
			amount: newVatAmount,
		},
		service_charge: {
			is_included: newServiceChargeIsIncluded,
			percentage: newServiceChargePercentage,
			amount: newServiceChargeAmount,
		},
		service_charge_bd: {
			is_included: newServiceChargeBdIsIncluded,
			percentage: newServiceChargeBdPercentage,
			amount: newServiceChargeBdAmount,
		},
		adjustment: {
			amount: subtotal_addons.adjustment.amount,
		},
		card_surcharge: {
			percentage: newCardSurchargePercentage,
			amount: newCardSurchargeAmount,
		},
		tax: {
			percentage: newTaxPercentage,
			amount: newTaxAmount,
		},
	};
};

export const $getOptionSubtotalAddons = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	option,
	item,
	isCancelled,
}: {
	option: OrderItemOptionInputLike<TOrderItemOption>;
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>;
	isCancelled: boolean;
}) => {
	if (isCancelled) return BASE_SUBTOTAL_ADDONS;

	const orderItem = $getItemMetadata(item);
	const orderItemOption = $getOptionMetadata(option, orderItem);

	const { subtotal: itemSubtotal, subtotal_addons: itemSubtotalAddons } =
		orderItem;
	const {
		original_price_addons: optionOriginalPriceAddons,
		subtotal: optionSubtotal,
		subtotal_addons: optionSubtotalAddons,
	} = orderItemOption;

	const optionPriceRatioToItem = bigMath.div(optionSubtotal, itemSubtotal);

	const newOfferIsCustomValue = itemSubtotalAddons.offer.is_custom_value;
	const newOfferMetadata = itemSubtotalAddons.offer.metadata;
	const itemOfferAmount = itemSubtotalAddons.offer.amount;
	const newOfferAmount = bigMath.mul(optionPriceRatioToItem, itemOfferAmount);

	const newDiscountIsDivided = true;
	const newDiscountType = itemSubtotalAddons.discount.type;
	const newDiscountValue = itemSubtotalAddons.discount.value;
	const newDiscountAmount = bigMath.mul(
		optionPriceRatioToItem,
		itemSubtotalAddons.discount.amount,
	);

	const newAdjustmentAmount = bigMath.mul(
		optionPriceRatioToItem,
		itemSubtotalAddons.adjustment.amount,
	);

	const newVatIsIncluded =
		!!optionOriginalPriceAddons.vat.percentage &&
		!!optionOriginalPriceAddons.vat.amount;
	const newVatPercentage = newVatIsIncluded
		? optionOriginalPriceAddons.vat.percentage
		: optionSubtotalAddons.vat.percentage;
	const newServiceChargeIsIncluded =
		!!optionOriginalPriceAddons.service_charge.percentage &&
		!!optionOriginalPriceAddons.service_charge.amount;
	const newServiceChargePercentage = newServiceChargeIsIncluded
		? optionOriginalPriceAddons.service_charge.percentage
		: optionSubtotalAddons.service_charge.percentage;
	const newServiceChargeBdIsIncluded =
		!!optionOriginalPriceAddons.service_charge_bd?.percentage &&
		!!optionOriginalPriceAddons.service_charge_bd?.amount;
	const newServiceChargeBdPercentage = newServiceChargeBdIsIncluded
		? (optionOriginalPriceAddons.service_charge_bd?.percentage ?? 0)
		: optionSubtotalAddons.service_charge_bd.percentage;
	const newCardSurchargePercentage =
		optionSubtotalAddons.card_surcharge.percentage;
	const newTaxPercentage = optionSubtotalAddons.tax?.percentage ?? 0;

	let [newVatAmount, newServiceChargeAmount, newServiceChargeBdAmount] = [
		0, 0, 0,
	];
	const grossSales = bigMath.sub(
		optionSubtotal,
		optionSubtotalAddons.offer.amount,
		optionSubtotalAddons.discount.amount,
	);
	const addedVatScRate = bigMath.add(
		newVatPercentage,
		newServiceChargePercentage,
	);
	newVatAmount = newVatIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newVatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					optionSubtotal,
					$getOptionTotalRawPriceAddons(orderItemOption),
					optionSubtotalAddons.offer.amount,
					optionSubtotalAddons.discount.amount,
				),
				newVatPercentage,
			);
	newVatAmount = getAmountWithinRange(0, newVatAmount, optionSubtotal);
	newServiceChargeAmount = newServiceChargeIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newServiceChargePercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					optionSubtotal,
					$getOptionTotalRawPriceAddons(orderItemOption),
					optionSubtotalAddons.offer.amount,
					optionSubtotalAddons.discount.amount,
				),
				newServiceChargePercentage,
			);
	newServiceChargeAmount = getAmountWithinRange(
		0,
		newServiceChargeAmount,
		optionSubtotal,
	);
	newServiceChargeBdAmount = newServiceChargeBdIsIncluded
		? new Big(
				new Big(
					new Big(optionSubtotal).sub(
						new Big(optionSubtotal).div(
							new Big(1).add(newServiceChargeBdPercentage),
						),
					),
				).mul(newServiceChargeBdPercentage),
			)
				.div(newServiceChargeBdPercentage || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					optionSubtotal,
					$getOptionTotalRawPriceAddons(orderItemOption),
				),
				newServiceChargeBdPercentage,
			);
	newServiceChargeBdAmount = getAmountWithinRange(
		0,
		newServiceChargeBdAmount,
		optionSubtotal,
	);
	let newTaxAmount = bigMath.mul(
		bigMath.add(
			bigMath.sub(
				optionSubtotal,
				$getItemTotalRawPriceAddons(item),
				optionSubtotalAddons.offer.amount,
				optionSubtotalAddons.discount.amount,
			),
			newVatAmount,
			newServiceChargeAmount,
			newServiceChargeBdAmount,
		),
		newTaxPercentage,
	);
	newTaxAmount = getAmountWithinRange(0, newTaxAmount, optionSubtotal);
	let newCardSurchargeAmount = bigMath.mul(
		bigMath.sub(
			optionSubtotal,
			$getItemTotalRawPriceAddons(item),
			optionSubtotalAddons.offer.amount,
			optionSubtotalAddons.discount.amount,
		),
		newCardSurchargePercentage,
	);
	newCardSurchargeAmount = getAmountWithinRange(
		0,
		newCardSurchargeAmount,
		optionSubtotal,
	);

	return {
		offer: {
			amount: newOfferAmount,
			metadata: newOfferAmount ? newOfferMetadata : undefined,
			is_custom_value: newOfferIsCustomValue,
		},
		discount: {
			is_divided: newDiscountIsDivided,
			type: newDiscountType,
			value: newDiscountValue,
			amount: newDiscountAmount,
		},
		vat: {
			is_included: newVatIsIncluded,
			percentage: newVatPercentage,
			amount: newVatAmount,
		},
		service_charge: {
			is_included: newServiceChargeIsIncluded,
			percentage: newServiceChargePercentage,
			amount: newServiceChargeAmount,
		},
		service_charge_bd: {
			is_included: newServiceChargeBdIsIncluded,
			percentage: newServiceChargeBdPercentage,
			amount: newServiceChargeBdAmount,
		},
		adjustment: {
			amount: newAdjustmentAmount,
		},
		card_surcharge: {
			percentage: newCardSurchargePercentage,
			amount: newCardSurchargeAmount,
		},
		tax: {
			percentage: newTaxPercentage,
			amount: newTaxAmount,
		},
	};
};
